import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, Search, Sparkles, Inbox, AlertTriangle, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useFeatureRequests,
  useFeatureRequestStatusCounts,
  useDeleteFeatureRequest,
} from '../../hooks/use-feature-requests';
import { useAuthStore } from '../../stores/auth-store';
import {
  FEATURE_REQUEST_STATUS_LABELS,
  FEATURE_REQUEST_STATUS_STYLES,
  FEATURE_REQUEST_TYPE_LABELS,
  FEATURE_REQUEST_TYPE_STYLES,
  EFFORT_LABELS,
  EFFORT_STYLES,
} from '../../lib/feature-request-constants';
import { timeAgo } from '../../lib/utils';
import { Dialog } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import type { FeatureRequestStatus, FeatureRequestType, FeatureRequest } from '../../types';

const STATUS_FILTERS: Array<FeatureRequestStatus | 'ALL'> = [
  'ALL',
  'IN_DISCUSSION',
  'AWAITING_APPROVAL',
  'APPROVED',
  'IN_PROGRESS',
  'IMPLEMENTED',
  'REJECTED',
  'DUPLICATE',
];

export function FeatureRequestsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isSuperAdmin = Boolean(useAuthStore((s) => s.user?.isSuperAdmin));
  const del = useDeleteFeatureRequest();
  const [status, setStatus] = useState<FeatureRequestStatus | 'ALL'>('ALL');
  const [type, setType] = useState<FeatureRequestType | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<FeatureRequest | null>(null);

  const { data, isLoading, isError, refetch } = useFeatureRequests({
    status: status === 'ALL' ? undefined : status,
    type: type === 'ALL' ? undefined : type,
    search: search || undefined,
  });
  const { data: countsData } = useFeatureRequestStatusCounts();
  const counts = countsData?.data ?? {};
  const items = data?.data ?? [];
  const total = data?.meta.pagination?.total ?? 0;

  const awaitingCount = counts.AWAITING_APPROVAL ?? 0;

  const filteredTypes: FeatureRequestType[] = ['FEATURE', 'CHANGE', 'BUG'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="app-kicker">Sugestii & îmbunătățiri</p>
          <h1 className="mt-1 text-2xl font-bold text-[color:var(--ink-strong)]">
            Cereri de funcționalitate
          </h1>
          <p className="mt-1.5 max-w-2xl text-sm text-[color:var(--ink-muted)]">
            Idei, modificări și buguri evaluate de asistentul AI. Doar super-adminii pot aproba sau
            respinge o cerere.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/feature-requests/new')}
          className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-white shadow-[var(--shadow-soft)] transition-all duration-200 hover:-translate-y-px hover:bg-primary-light"
        >
          <Lightbulb size={17} />
          Sugestie nouă
        </button>
      </div>

      {/* Awaiting-approval highlight */}
      {awaitingCount > 0 && (
        <button
          type="button"
          onClick={() => setStatus('AWAITING_APPROVAL')}
          className="flex w-full items-center gap-3 rounded-[1.25rem] border border-accent/30 bg-accent/8 px-5 py-3.5 text-left transition-colors hover:bg-accent/14"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/18">
            <Sparkles size={17} className="text-accent-dark" />
          </span>
          <span className="text-sm text-[color:var(--ink-base)]">
            <strong className="font-semibold text-[color:var(--ink-strong)]">
              {awaitingCount} {awaitingCount === 1 ? 'cerere așteaptă' : 'cereri așteaptă'}
            </strong>{' '}
            aprobarea ta. Apasă pentru a le vedea.
          </span>
        </button>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((s) => {
            const active = status === s;
            const count = s === 'ALL' ? total : (counts[s] ?? 0);
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                aria-pressed={active}
                className={
                  active
                    ? 'rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary'
                    : 'rounded-full border border-[color:var(--surface-line)] bg-[var(--surface-panel)] px-3.5 py-1.5 text-xs font-medium text-[color:var(--ink-muted)] transition-colors hover:border-[color:var(--surface-line-strong)] hover:text-[color:var(--ink-strong)]'
                }
              >
                {s === 'ALL' ? 'Toate' : FEATURE_REQUEST_STATUS_LABELS[s]}
                <span className={active ? 'ml-1.5 text-primary/70' : 'ml-1.5 text-[color:var(--ink-soft)]'}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-[color:var(--surface-line)] bg-[var(--surface-panel)] p-0.5">
            {(['ALL', ...filteredTypes] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t as FeatureRequestType | 'ALL')}
                aria-pressed={type === t}
                className={
                  type === t
                    ? 'rounded-full bg-[var(--surface-panel-strong)] px-3 py-1 text-xs font-semibold text-[color:var(--ink-strong)] shadow-sm'
                    : 'rounded-full px-3 py-1 text-xs font-medium text-[color:var(--ink-muted)] hover:text-[color:var(--ink-strong)]'
                }
              >
                {t === 'ALL' ? 'Toate tipurile' : FEATURE_REQUEST_TYPE_LABELS[t as FeatureRequestType]}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[color:var(--ink-soft)]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută…"
              aria-label="Caută cereri de funcționalitate"
              className="h-9 w-44 rounded-full border border-[color:var(--surface-line)] bg-[var(--surface-panel)] pl-9 pr-4 text-sm text-[color:var(--ink-strong)] placeholder:text-[color:var(--ink-soft)] focus:border-[color:var(--surface-line-strong)] focus:outline-none focus:ring-2 focus:ring-[color:var(--surface-focus)]"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="app-shell-panel overflow-hidden rounded-[1.5rem]">
        <div className="overflow-x-auto">
          <table className="w-full" style={{ tableLayout: 'fixed' }}>
            <thead className="bg-[var(--surface-panel-strong)]">
              <tr className="border-b border-[color:var(--surface-line)]">
                <th className="w-[38%] px-5 py-3 text-left text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[color:var(--ink-soft)]">
                  Cerere
                </th>
                <th className="w-[17%] px-4 py-3 text-left text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[color:var(--ink-soft)]">
                  Status
                </th>
                <th className="w-[12%] px-4 py-3 text-left text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[color:var(--ink-soft)]">
                  Efort
                </th>
                <th className="w-[16%] px-4 py-3 text-left text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[color:var(--ink-soft)]">
                  Propus de
                </th>
                <th className="w-[10%] px-4 py-3 text-left text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[color:var(--ink-soft)]">
                  Data
                </th>
                <th className="w-[7%] px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--surface-line)]">
              {isLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    <td colSpan={6} className="px-5 py-4">
                      <div className="h-4 w-full animate-pulse rounded bg-[var(--surface-panel-strong)]" />
                    </td>
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={6} className="px-5 py-16">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-error/10">
                        <AlertTriangle size={24} className="text-error" />
                      </span>
                      <p className="text-sm font-medium text-[color:var(--ink-base)]">
                        Nu am putut încărca cererile
                      </p>
                      <p className="max-w-sm text-xs text-[color:var(--ink-muted)]">
                        A apărut o eroare la încărcarea listei. Verifică conexiunea și încearcă
                        din nou.
                      </p>
                      <button
                        type="button"
                        onClick={() => refetch()}
                        className="mt-1 inline-flex h-9 items-center justify-center rounded-full bg-primary px-4 text-xs font-semibold text-white transition-colors hover:bg-primary-light"
                      >
                        Reîncearcă
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {!isLoading && !isError && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-16">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--surface-panel-strong)]">
                        <Inbox size={24} className="text-[color:var(--ink-soft)]" />
                      </span>
                      <p className="text-sm font-medium text-[color:var(--ink-base)]">
                        Nicio cerere găsită
                      </p>
                      <p className="max-w-sm text-xs text-[color:var(--ink-muted)]">
                        Filtrele curente nu au rezultate. Propune prima idee cu butonul „Sugestie
                        nouă” — asistentul AI o va evalua critic.
                      </p>
                    </div>
                  </td>
                </tr>
              )}

              {items.map((item) => (
                <tr
                  key={item.id}
                  tabIndex={0}
                  onClick={() => navigate(`/admin/feature-requests/${item.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/admin/feature-requests/${item.id}`);
                    }
                  }}
                  className="cursor-pointer transition-colors hover:bg-[var(--surface-panel-strong)]/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--surface-focus)]"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${FEATURE_REQUEST_TYPE_STYLES[item.type]}`}
                      >
                        {FEATURE_REQUEST_TYPE_LABELS[item.type].toUpperCase()}
                      </span>
                      <span className="truncate text-sm font-medium text-[color:var(--ink-strong)]">
                        {item.title}
                      </span>
                    </div>
                    {item.problem && (
                      <p className="mt-1 truncate text-xs text-[color:var(--ink-muted)]">
                        {item.problem}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${FEATURE_REQUEST_STATUS_STYLES[item.status]}`}
                    >
                      {FEATURE_REQUEST_STATUS_LABELS[item.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    {item.effortEstimate ? (
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${EFFORT_STYLES[item.effortEstimate]}`}
                      >
                        {EFFORT_LABELS[item.effortEstimate]}
                      </span>
                    ) : (
                      <span className="text-xs text-[color:var(--ink-soft)]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="truncate text-sm text-[color:var(--ink-base)]">
                      {item.proposer?.name ?? '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-xs text-[color:var(--ink-muted)]">{timeAgo(item.createdAt)}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs text-[color:var(--ink-soft)]">
                        {item._count.messages > 0 ? `${item._count.messages} msg` : ''}
                      </span>
                      {isSuperAdmin && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(item);
                          }}
                          aria-label={`Șterge cererea ${item.title}`}
                          title="Șterge cererea"
                          className="rounded-full p-1.5 text-[color:var(--ink-soft)] transition-colors hover:bg-error/10 hover:text-error"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refresh hint */}
      <p className="text-center text-xs text-[color:var(--ink-soft)]">
        Lista se reîmprospătează automat.{' '}
        <button
          type="button"
          onClick={() => qc.invalidateQueries({ queryKey: ['feature-requests'] })}
          className="font-medium text-primary hover:underline"
        >
          Reîmprospătează acum
        </button>
      </p>

      {/* Delete confirm dialog */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} title="Șterge cererea">
        <div className="space-y-4">
          <p className="text-sm text-[color:var(--ink-muted)]">
            Ești sigur că vrei să ștergi cererea{' '}
            <strong className="font-medium text-[color:var(--ink-strong)]">
              „{deleteTarget?.title}”
            </strong>
            ? Conversația cu AI-ul și istoricul de status vor fi șterse definitiv. Această acțiune
            nu poate fi anulată.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Anulează
            </Button>
            <Button
              variant="danger"
              disabled={del.isPending}
              onClick={() => {
                if (deleteTarget) del.mutate(deleteTarget.id);
              }}
            >
              <Trash2 size={16} /> Șterge definitiv
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
